import React, { useState, useRef, useEffect } from 'react';
import { slideApiService } from '../services/slideApiService';
import { QuickAnalyzeResponse, MarketplaceEvalResponse, RateLimitError } from '../types';
import { useShop } from '../contexts/ShopContext';
import MPESearchModal from './MPESearchModal';
import MPEResults from './MPEResults';
import RateLimitModal from './RateLimitModal';

interface QuickAnalyzeTabProps {
  shopId: string;
  onSaveAndEdit?: (slideId: string) => void;
}

const QuickAnalyzeTab: React.FC<QuickAnalyzeTabProps> = ({ shopId, onSaveAndEdit }) => {
  const { currentShop } = useShop();
  const [capturedImages, setCapturedImages] = useState<File[]>([]);
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [userText, setUserText] = useState<string>('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<QuickAnalyzeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rateLimitError, setRateLimitError] = useState<RateLimitError | null>(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [showSaveMenu, setShowSaveMenu] = useState(false);
  const [showMPEModal, setShowMPEModal] = useState(false);
  const [mpeResults, setMPEResults] = useState<MarketplaceEvalResponse | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const saveMenuRef = useRef<HTMLDivElement>(null);
  const imageUrlsRef = useRef<string[]>([]);

  // Cleanup camera stream on unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  // Keep ref in sync with imageUrls state
  useEffect(() => {
    imageUrlsRef.current = imageUrls;
  }, [imageUrls]);

  // Cleanup object URLs only on unmount
  useEffect(() => {
    return () => {
      // Revoke all URLs on unmount
      imageUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
    };
  }, []);

  // Cleanup long press timer on unmount
  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    };
  }, []);

  // Handle window resize to update mobile state
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (saveMenuRef.current && !saveMenuRef.current.contains(event.target as Node)) {
        setShowSaveMenu(false);
      }
    };

    if (showSaveMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSaveMenu]);

  const handleSaveButtonPress = () => {
    if (isAnalyzing) return;

    // Start timer for long press (400ms)
    longPressTimerRef.current = setTimeout(() => {
      setShowSaveMenu(true);
    }, 400);
  };

  const handleSaveButtonRelease = () => {
    if (isAnalyzing) return;

    // If timer is still running, it was a short press
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;

      // Only do regular save if menu isn't showing
      if (!showSaveMenu) {
        convertToSlide(false);
      }
    }
  };

  // Attach stream to video element when camera is active and stream is available
  useEffect(() => {
    if (isCameraActive && stream) {
      const timer = setTimeout(() => {
        if (videoRef.current) {
          const videoElement = videoRef.current;
          videoElement.srcObject = stream;
          videoElement.play().catch(err => {
            console.error('Failed to play video:', err);
          });
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isCameraActive, stream]);

  const startCamera = async () => {
    try {
      setError(null);

      // Try with facingMode first (mobile), fallback to basic video for laptops
      let mediaStream: MediaStream | null = null;

      try {
        // Try mobile-optimized settings first
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'environment',
            width: { ideal: 1920 },
            height: { ideal: 1080 }
          }
        });
      } catch (mobileErr) {
        // Fallback to basic video for laptops (no facingMode)
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: true
        });
      }

      if (!mediaStream) {
        throw new Error('Failed to acquire camera stream');
      }

      setStream(mediaStream);
      setIsCameraActive(true);
      // useEffect will handle attaching stream to video element
    } catch (err) {
      setError('Failed to access camera. Please check permissions or try "Choose from Photos".');
      console.error('Camera error:', err);
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setIsCameraActive(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) return;

    // Set canvas size to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert canvas to blob
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
        const url = URL.createObjectURL(file);
        setCapturedImages(prev => [...prev, file]);
        setImageUrls(prev => [...prev, url]);
      }
    }, 'image/jpeg', 0.92);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      const newFiles = [...capturedImages, ...files].slice(0, 10);
      const newUrls = files.map(file => URL.createObjectURL(file));

      setCapturedImages(newFiles);
      setImageUrls(prev => [...prev, ...newUrls].slice(0, 10));

      // Reset file input to allow re-selecting the same file
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeImage = (index: number) => {
    // Revoke the object URL to free memory
    if (imageUrls[index]) {
      URL.revokeObjectURL(imageUrls[index]);
    }

    setCapturedImages(prev => prev.filter((_, i) => i !== index));
    setImageUrls(prev => prev.filter((_, i) => i !== index));
  };

  const analyzeImages = async () => {
    if (capturedImages.length === 0) {
      setError('Please capture or select at least one image');
      return;
    }

    console.log('Analyzing images with shopId:', shopId);

    if (!shopId) {
      setError('Shop ID is missing. Please refresh the page and try again.');
      return;
    }

    try {
      setIsAnalyzing(true);
      setError(null);

      console.log('Triggering analysis for shop:', shopId);
      console.log('Number of images:', capturedImages.length);
      console.log('User text:', userText || '(none)');

      // Trigger analysis
      const triggerResponse = await slideApiService.triggerQuickAnalyze(
        shopId,
        capturedImages,
        userText.trim() || undefined
      );
      console.log('Trigger response:', triggerResponse);

      // Poll for results (simple polling every 2 seconds, max 30 seconds)
      const maxAttempts = 15;
      let attempts = 0;

      const pollResults = async (): Promise<QuickAnalyzeResponse> => {
        attempts++;

        try {
          // Using analysis_id from TriggerAnalysisResponse (snake_case from backend)
          const result = await slideApiService.getQuickAnalyzeResults(shopId, triggerResponse.analysis_id);
          return result;
        } catch (err) {
          if (attempts < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, 2000));
            return pollResults();
          }
          throw new Error('Analysis timeout. Please try again.');
        }
      };

      const result = await pollResults();
      setAnalysisResult(result);
      stopCamera();

    } catch (err) {
      console.error('Full analysis error:', err);

      // Handle rate limit errors specially
      if (err instanceof RateLimitError) {
        setRateLimitError(err);
        setError(null);
      } else {
        const errorMessage = err instanceof Error ? err.message : 'Failed to analyze images';
        console.error('Error message:', errorMessage);
        setError(errorMessage);
        setRateLimitError(null);
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  const startNewAnalysis = () => {
    // Revoke all object URLs
    imageUrls.forEach(url => URL.revokeObjectURL(url));

    setAnalysisResult(null);
    setCapturedImages([]);
    setImageUrls([]);
    setUserText('');
    setError(null);
    setRateLimitError(null);
    setMPEResults(null);
    setShowMPEModal(false);
  };

  const convertToSlide = async (openEditModal: boolean = false) => {
    if (!analysisResult) return;

    try {
      setIsAnalyzing(true);
      setError(null);
      setShowSaveMenu(false); // Close menu

      // Build categories from analysis result — prefer categoryTags (search-optimized)
      const categories: string[] = [];

      if (analysisResult.categoryTags && analysisResult.categoryTags.length > 0) {
        analysisResult.categoryTags.forEach(tag => {
          const t = tag.toUpperCase();
          if (t.length <= 50) {
            categories.push(t);
          }
        });
      } else {
        // Fallback for pre-categoryTags analyses
        if (analysisResult.suggestedEra) {
          const era = analysisResult.suggestedEra.toUpperCase();
          if (era.length <= 50) {
            categories.push(era);
          }
        }

        if (analysisResult.stylePeriod) {
          const style = analysisResult.stylePeriod.toUpperCase();
          if (style.length <= 50) {
            categories.push(style);
          }
        }

        if (analysisResult.marketplaceKeywords) {
          analysisResult.marketplaceKeywords.forEach(keyword => {
            const kw = keyword.toUpperCase();
            if (kw.length <= 50) {
              categories.push(kw);
            }
          });
        }
      }

      // Deduplicate categories
      const uniqueCategories = Array.from(new Set(categories));

      const newSlide = await slideApiService.convertQuickAnalyzeToSlide(
        shopId,
        analysisResult.analysisId,
        uniqueCategories.length > 0 ? { categories: uniqueCategories } : undefined
      );

      if (openEditModal && onSaveAndEdit) {
        // Save & Edit mode - switch to slides tab and open edit modal
        onSaveAndEdit(newSlide.id);
      } else {
        // Regular save mode - reset to start new analysis
        startNewAnalysis();
      }

    } catch (err) {
      console.error('Convert error:', err);
      if (err instanceof RateLimitError) {
        setRateLimitError(err);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to save to inventory');
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Show analysis results
  if (analysisResult) {
    return (
      <div style={{
        backgroundColor: 'var(--bg-primary)',
        borderRadius: '8px',
        padding: '24px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '8px' }}>
            Analysis Results
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
            AI-powered assessment • {analysisResult.imageCount} images analyzed
          </p>
        </div>

        {/* Image Gallery (v17: Display only, no selection) */}
        {capturedImages.length > 0 && (
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '12px' }}>
              📸 Analyzed Images
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
              gap: '12px'
            }}>
              {imageUrls.map((url, index) => (
                <div
                  key={index}
                  style={{
                    position: 'relative'
                  }}
                >
                  <img
                    src={url}
                    alt={`Analyzed image ${index + 1}`}
                    style={{
                      width: '100%',
                      height: '150px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      border: '2px solid var(--border-light)'
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Display user's input text if provided */}
        {analysisResult.userProvidedText && (
          <div style={{
            marginBottom: '24px',
            padding: '16px',
            backgroundColor: 'var(--bg-primary)',
            borderRadius: '8px',
            borderLeft: '4px solid var(--primary-light)'
          }}>
            <h4 style={{
              fontSize: '14px',
              fontWeight: 600,
              marginBottom: '8px',
              color: 'var(--text-primary)'
            }}>
              Your Input:
            </h4>
            <p style={{
              fontSize: '14px',
              margin: 0,
              whiteSpace: 'pre-wrap',
              color: 'var(--text-primary)'
            }}>
              {analysisResult.userProvidedText}
            </p>
          </div>
        )}

        <div style={{ marginBottom: '32px' }}>
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '12px' }}>
              🎯 Quick Assessment
            </h3>
            {analysisResult.suggestedTitle && (
              <p style={{ marginBottom: '8px' }}>
                <strong>Title:</strong> {analysisResult.suggestedTitle}
              </p>
            )}
            {analysisResult.suggestedEra && (
              <p style={{ marginBottom: '8px' }}>
                <strong>Era:</strong> {analysisResult.suggestedEra}
              </p>
            )}
            {analysisResult.suggestedMaker && (
              <p style={{ marginBottom: '8px' }}>
                <strong>Maker:</strong> {analysisResult.suggestedMaker}
              </p>
            )}
            {analysisResult.stylePeriod && (
              <p style={{ marginBottom: '8px' }}>
                <strong>Style:</strong> {analysisResult.stylePeriod}
              </p>
            )}
          </div>

          {/* Price Tag Detection (v14) */}
          {analysisResult.priceTagDetected && analysisResult.detectedPrice && (
            <div style={{
              marginBottom: '24px',
              padding: '16px',
              backgroundColor: 'var(--bg-accent)',
              borderRadius: '8px',
              borderLeft: '4px solid var(--success)'
            }}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '12px' }}>
                💰 Price Tag Detected
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <p style={{ marginBottom: '4px', fontSize: '1.125rem', fontWeight: 600, color: 'var(--success-dark)' }}>
                  {analysisResult.detectedCurrency || 'USD'} ${analysisResult.detectedPrice.toFixed(2)}
                </p>
                {analysisResult.detectedRetailer && (
                  <p style={{ marginBottom: '4px', color: 'var(--text-primary)' }}>
                    <strong>Store:</strong> {analysisResult.detectedRetailer}
                  </p>
                )}
                {typeof analysisResult.priceTagImageIndex === 'number' && (
                  <p style={{ marginBottom: '0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                    Price tag visible in image {analysisResult.priceTagImageIndex + 1}
                  </p>
                )}
              </div>
            </div>
          )}

          {analysisResult.distinguishingFeatures && (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '12px' }}>
                ✨ Distinguishing Features
              </h3>
              <p style={{ color: 'var(--text-primary)' }}>{analysisResult.distinguishingFeatures}</p>
            </div>
          )}

          {analysisResult.conditionNotes && (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '12px' }}>
                📝 Condition
              </h3>
              <p style={{ color: 'var(--text-primary)' }}>{analysisResult.conditionNotes}</p>
            </div>
          )}

          {analysisResult.collectibilityNotes && (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '12px' }}>
                💎 Collectibility
              </h3>
              <p style={{ color: 'var(--text-primary)' }}>{analysisResult.collectibilityNotes}</p>
            </div>
          )}

          {/* Marketplace Keywords */}
          {analysisResult.marketplaceKeywords && analysisResult.marketplaceKeywords.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '12px' }}>
                🏷️ Marketplace Keywords
              </h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {analysisResult.marketplaceKeywords.map((keyword, index) => (
                  <span
                    key={index}
                    style={{
                      padding: '4px 12px',
                      backgroundColor: 'var(--border-light)',
                      borderRadius: '12px',
                      fontSize: '0.875rem',
                      color: 'var(--text-primary)',
                    }}
                  >
                    {keyword}
                  </span>
                ))}
              </div>
            </div>
          )}

          {analysisResult.imageSearchQueries && analysisResult.imageSearchQueries.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '12px' }}>
                🔍 Search Suggestions
              </h3>
              <ul style={{ listStyle: 'disc', paddingLeft: '20px' }}>
                {analysisResult.imageSearchQueries.map((query, index) => (
                  <li key={index} style={{ marginBottom: '4px', color: 'var(--text-primary)' }}>{query}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {error && (
          <div style={{
            backgroundColor: 'var(--error-bg)',
            border: '1px solid var(--error-border)',
            borderRadius: '4px',
            padding: '12px',
            marginBottom: '16px',
            color: 'var(--error-text)'
          }}>
            {error}
          </div>
        )}

        {/* Rate Limit Modal */}
        {rateLimitError && (
          <RateLimitModal
            error={rateLimitError}
            onClose={() => setRateLimitError(null)}
          />
        )}

        {/* Marketplace Eval Button (all tiers - usage controlled by rate limits) */}
        {analysisResult.imageSearchQueries && analysisResult.imageSearchQueries.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <button
              onClick={() => setShowMPEModal(true)}
              disabled={isAnalyzing}
              style={{
                width: '100%',
                backgroundColor: 'var(--primary)',
                color: 'var(--text-inverse)',
                padding: '12px 24px',
                borderRadius: '6px',
                border: 'none',
                fontWeight: 600,
                cursor: isAnalyzing ? 'not-allowed' : 'pointer',
                opacity: isAnalyzing ? 0.6 : 1,
                fontSize: '0.9rem',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => !isAnalyzing && (e.currentTarget.style.backgroundColor = 'var(--primary)')}
              onMouseLeave={(e) => !isAnalyzing && (e.currentTarget.style.backgroundColor = 'var(--primary)')}
            >
              🛍️ Run Marketplace Eval
            </button>
          </div>
        )}

        {/* Marketplace Eval Results */}
        {mpeResults && (
          <MPEResults
            response={mpeResults}
            onClose={() => setMPEResults(null)}
          />
        )}

        <div style={{ display: 'flex', gap: '12px', position: 'relative' }}>
          <div style={{ flex: 1, position: 'relative' }} ref={saveMenuRef}>
            <button
              onMouseDown={handleSaveButtonPress}
              onMouseUp={handleSaveButtonRelease}
              onMouseLeave={handleSaveButtonRelease}
              onTouchStart={handleSaveButtonPress}
              onTouchEnd={handleSaveButtonRelease}
              disabled={isAnalyzing}
              title="Click to Save, or Hold for Options"
              style={{
                width: '100%',
                backgroundColor: 'var(--primary)',
                color: 'var(--text-inverse)',
                padding: '12px 24px',
                borderRadius: '6px',
                border: 'none',
                fontWeight: 600,
                cursor: isAnalyzing ? 'not-allowed' : 'pointer',
                opacity: isAnalyzing ? 0.6 : 1,
                transition: 'background-color 0.2s'
              }}
            >
              {isAnalyzing ? 'Saving...' : '✅ Save as Slide'}
            </button>

            {/* Context Menu */}
            {showSaveMenu && !isAnalyzing && (
              <div style={{
                position: 'absolute',
                bottom: '100%',
                left: 0,
                right: 0,
                marginBottom: '8px',
                backgroundColor: 'var(--bg-card)',
                border: '2px solid var(--primary)',
                borderRadius: '6px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                overflow: 'hidden',
                zIndex: 1000
              }}>
                <button
                  onClick={() => convertToSlide(false)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: 'none',
                    backgroundColor: 'var(--bg-card)',
                    color: 'var(--text-primary)',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    borderBottom: '1px solid var(--border-light)'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-primary)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  <span>✅</span>
                  <span>Save as Slide</span>
                </button>
                <button
                  onClick={() => convertToSlide(true)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: 'none',
                    backgroundColor: 'var(--bg-card)',
                    color: 'var(--text-primary)',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-primary)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  <span>✏️</span>
                  <span>Save as Slide and Edit</span>
                </button>
              </div>
            )}
          </div>

          <button
            onClick={startNewAnalysis}
            disabled={isAnalyzing}
            style={{
              flex: 1,
              backgroundColor: 'var(--border-light)',
              color: 'var(--text-primary)',
              padding: '12px 24px',
              borderRadius: '6px',
              border: 'none',
              fontWeight: 600,
              cursor: isAnalyzing ? 'not-allowed' : 'pointer',
              opacity: isAnalyzing ? 0.6 : 1
            }}
          >
            🗑️ Discard
          </button>
        </div>

        {/* Marketplace Eval Modal (v17: Pass image URLs for in-modal selection) */}
        {showMPEModal && analysisResult && (
          <MPESearchModal
            analysisId={analysisResult.analysisId}
            shopId={shopId}
            suggestedQuery={analysisResult.imageSearchQueries?.[0] || ''}
            imageUrls={imageUrls}
            onClose={() => setShowMPEModal(false)}
            onResults={(response) => {
              setMPEResults(response);
              setShowMPEModal(false);
            }}
          />
        )}
      </div>
    );
  }

  // Show camera capture interface
  return (
    <>
      {/* Full-screen camera overlay for mobile */}
      {isCameraActive && isMobile && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'var(--photo-backdrop)',
          zIndex: 10000,
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Header with photo counter and Close button */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            zIndex: 10
          }}>
            <div style={{ fontSize: '14px', fontWeight: 500 }}>
              {capturedImages.length} photos
            </div>
            <button
              onClick={stopCamera}
              type="button"
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                color: 'white',
                cursor: 'pointer',
                padding: '0'
              }}
            >
              ✕
            </button>
          </div>

          {/* Camera viewport */}
          <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              webkit-playsinline="true"
              x-webkit-airplay="deny"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
                backgroundColor: 'var(--photo-backdrop)'
              }}
            />
            <canvas ref={canvasRef} style={{ display: 'none' }} />

            {/* Capture button */}
            <button
              type="button"
              onClick={capturePhoto}
              style={{
                position: 'absolute',
                bottom: '30px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'var(--primary)',
                color: 'var(--text-inverse)',
                padding: '12px 24px',
                fontSize: '1.125rem',
                borderRadius: '50%',
                border: '4px solid white',
                width: '70px',
                height: '70px',
                fontWeight: 600,
                cursor: 'pointer',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
                zIndex: 10
              }}
            >
              📷
            </button>
          </div>
        </div>
      )}

      <div style={{
        backgroundColor: 'var(--bg-primary)',
        borderRadius: '8px',
        padding: '24px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
      }}>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '8px' }}>
          Quick Analyze
        </h2>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
          Snap photos for instant AI assessment
        </p>
      </div>

      {error && (
        <div style={{
          backgroundColor: 'var(--error-bg)',
          border: '1px solid var(--error-border)',
          borderRadius: '4px',
          padding: '12px',
          marginBottom: '16px',
          color: 'var(--error-text)'
        }}>
          {error}
        </div>
      )}

      {/* Rate Limit Modal */}
      {rateLimitError && (
        <RateLimitModal
          error={rateLimitError}
          onClose={() => setRateLimitError(null)}
        />
      )}

      {!isCameraActive ? (
        <div style={{ padding: '20px' }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            gap: '8px',
            alignItems: 'center'
          }}>
            <button
              onClick={startCamera}
              style={{
                width: '100%',
                backgroundColor: 'var(--primary)',
                color: 'var(--text-inverse)',
                padding: '10px 8px',
                fontSize: '0.8rem',
                borderRadius: '6px',
                border: 'none',
                fontWeight: 600,
                cursor: 'pointer',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              📸 Camera
            </button>

            <div style={{
              color: 'var(--text-secondary)',
              fontSize: '0.75rem',
              fontWeight: 500,
              whiteSpace: 'nowrap'
            }}>OR</div>

            <button
              onClick={() => fileInputRef.current?.click()}
              style={{
                width: '100%',
                backgroundColor: 'var(--border-light)',
                color: 'var(--text-primary)',
                padding: '10px 8px',
                fontSize: '0.8rem',
                borderRadius: '6px',
                border: 'none',
                fontWeight: 600,
                cursor: 'pointer',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              📁 Photos
            </button>
          </div>
        </div>
      ) : !isMobile && (
        <div style={{
          position: 'relative',
          backgroundColor: 'var(--photo-backdrop)',
          borderRadius: '8px',
          overflow: 'visible',
          marginBottom: '16px',
          width: '100%',
          maxWidth: '640px',
          margin: '0 auto 16px'
        }}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{
              width: '100%',
              height: 'auto',
              minHeight: window.innerHeight > window.innerWidth ? '480px' : '60vh',
              maxHeight: window.innerHeight > window.innerWidth ? 'none' : '75vh',
              objectFit: 'cover',
              display: 'block',
              backgroundColor: 'var(--photo-backdrop)'
            }}
          />
          <canvas ref={canvasRef} style={{ display: 'none' }} />

          {/* Close button */}
          <button
            onClick={stopCamera}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              fontSize: '1.5rem',
              cursor: 'pointer',
              zIndex: 10
            }}
          >
            ✕
          </button>

          {/* Capture button - overlaid at bottom center */}
          <button
            onClick={capturePhoto}
            disabled={capturedImages.length >= 10}
            style={{
              position: 'absolute',
              bottom: '20px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'var(--primary)',
              color: 'var(--text-inverse)',
              padding: '16px 32px',
              fontSize: '1.125rem',
              borderRadius: '50%',
              border: '4px solid white',
              width: '80px',
              height: '80px',
              fontWeight: 600,
              cursor: capturedImages.length >= 10 ? 'not-allowed' : 'pointer',
              opacity: capturedImages.length >= 10 ? 0.5 : 1,
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
              zIndex: 10
            }}
          >
            📷
          </button>
        </div>
      )}

      {capturedImages.length > 0 && (
        <div style={{ marginTop: '24px' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '12px' }}>
            Captured Photos ({capturedImages.length}/10)
          </h3>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
            gap: '8px',
            marginBottom: '16px'
          }}>
            {imageUrls.map((url, index) => (
              <div key={index} style={{ position: 'relative' }}>
                <img
                  src={url}
                  alt={`Captured ${index + 1}`}
                  style={{
                    width: '100%',
                    height: '100px',
                    objectFit: 'cover',
                    borderRadius: '4px'
                  }}
                />
                <button
                  onClick={() => removeImage(index)}
                  style={{
                    position: 'absolute',
                    top: '4px',
                    right: '4px',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    fontSize: '0.875rem',
                    cursor: 'pointer'
                  }}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>

          {/* User Text Input */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 500,
              fontSize: '0.875rem',
              color: 'var(--text-primary)'
            }}>
              Additional Information (Optional)
            </label>
            <textarea
              value={userText}
              onChange={(e) => setUserText(e.target.value)}
              placeholder="Describe the item, ask questions, or provide context that might help with analysis... (e.g., 'Found at estate sale, has marking on bottom', 'Is this authentic?', 'What era is this from?')"
              rows={4}
              maxLength={2000}
              disabled={isAnalyzing}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid var(--border-light)',
                borderRadius: '6px',
                fontSize: '16px',
                resize: 'vertical',
                fontFamily: 'inherit',
                lineHeight: '1.5',
                boxSizing: 'border-box'
              }}
            />
            <div style={{
              fontSize: '12px',
              color: 'var(--text-secondary)',
              marginTop: '4px',
              textAlign: 'right'
            }}>
              {userText.length}/2000 characters
            </div>
          </div>

          <button
            onClick={analyzeImages}
            disabled={isAnalyzing || capturedImages.length === 0}
            style={{
              width: '100%',
              backgroundColor: 'var(--primary)',
              color: 'var(--text-inverse)',
              padding: '14px 24px',
              fontSize: '1rem',
              borderRadius: '6px',
              border: 'none',
              fontWeight: 600,
              cursor: (isAnalyzing || capturedImages.length === 0) ? 'not-allowed' : 'pointer',
              opacity: (isAnalyzing || capturedImages.length === 0) ? 0.6 : 1
            }}
          >
            {isAnalyzing ? '🔄 Analyzing...' : `✓ Analyze (${capturedImages.length} photos)`}
          </button>
        </div>
      )}
    </div>
    </>
  );
};

export default QuickAnalyzeTab;
